import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "react-hot-toast";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { SmsQuietHoursDialog } from "@/components/SmsQuietHoursDialog";
import { triggerQuietHoursPrompt } from "@/lib/smsQuietHoursBridge";

// Pages
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import RoleSelect from "@/pages/RoleSelect";
import StudentDashboard from "@/pages/StudentDashboard";
import TeacherDashboard from "@/pages/TeacherDashboard";
import Assignments from "@/pages/Assignments";
import AssignmentDetail from "@/pages/AssignmentDetail";
import Journals from "@/pages/Journals";
import JournalEditor from "@/pages/JournalEditor";
import Forum from "@/pages/Forum";
import ForumPost from "@/pages/ForumPost";
import Messages from "@/pages/Messages";
import Meetings from "@/pages/Meetings";
import MeetingDetail from "@/pages/MeetingDetail";
import AvailabilitySettings from "@/pages/AvailabilitySettings";
import BookMeeting from "@/pages/BookMeeting";
import Students from "@/pages/Students";
import StudentProfile from "@/pages/StudentProfile";
import Teachers from "@/pages/Teachers";
import Profile from "@/pages/Profile";
import WeeklyCheckIn from "@/pages/WeeklyCheckIn";
import CheckIns from "@/pages/CheckIns";
import CheckInHistory from "@/pages/CheckInHistory";
import CheckInFieldEditor from "@/pages/CheckInFieldEditor";
import Versions from "@/pages/Versions";
import NotificationCenter from "@/pages/NotificationCenter";
import MaintenancePage from "@/pages/MaintenancePage";
import AppSettings from "@/pages/AppSettings";
import Admin from "@/pages/Admin";

import SmsAdmin from "@/pages/SmsAdmin";
import Home from "@/pages/Home";
import Community from "@/pages/Community";
import CommunityNotifications from "@/pages/CommunityNotifications";
import CommunityMessages from "@/pages/CommunityMessages";
import CommunityProfile from "@/pages/CommunityProfile";
import PlanningCenter from "@/pages/PlanningCenter";
import Events from "@/pages/Events";
import WeeklyGuide from "@/pages/WeeklyGuide";
import WeeklyGuideAdmin from "@/pages/WeeklyGuideAdmin";
import GuideArchive from "@/pages/GuideArchive";
import WednesdayNights from "@/pages/WednesdayNights";
import WednesdayArchive from "@/pages/WednesdayArchive";
import WednesdayProgress from "@/pages/WednesdayProgress";
import WednesdayAdmin from "@/pages/WednesdayAdmin";
import GrowthPlan from "@/pages/GrowthPlan";

// Google OAuth Client ID
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Determine backend URL - always use same origin for API calls
// This works for any domain (custom domains, emergent.host, preview, localhost)
const BACKEND_URL = window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// SMS Quiet Hours gate: any request that would text during 9PM-8AM America/Chicago
// gets a 428 from the backend with {quiet_hours, message, send_at_local}. Intercept it,
// show the global prompt (Send Now / Wait Until 8AM), then retry the exact same request
// with the user's decision attached — so every SMS-triggering page gets this for free.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const resp = error.response;
    const payload = resp?.data?.detail;
    if (resp && resp.status === 428 && payload && payload.quiet_hours) {
      const decision = await triggerQuietHoursPrompt(payload);
      if (!decision) {
        return Promise.reject(error);
      }
      const config = error.config;
      if (config.data) {
        try {
          const parsed = typeof config.data === "string" ? JSON.parse(config.data) : config.data;
          parsed.sms_decision = decision;
          config.data = JSON.stringify(parsed);
        } catch (e) {
          config.data = JSON.stringify({ sms_decision: decision });
        }
      } else {
        const sep = config.url.includes("?") ? "&" : "?";
        config.url = `${config.url}${sep}sms_decision=${decision}`;
      }
      return axios(config);
    }
    return Promise.reject(error);
  }
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data was passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/");
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-300 border-t-slate-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children({ user, setUser });
};

// All standard authenticated pages: [path, PageComponent]
// Each renders inside ProtectedRoute and receives the `user` prop.
const PROTECTED_PAGES = [
  // Discipleship Intensive
  ["/student", StudentDashboard],
  ["/teacher", TeacherDashboard],
  ["/students", Students],
  ["/students/:studentId", StudentProfile],
  ["/assignments", Assignments],
  ["/assignments/:id", AssignmentDetail],
  ["/journals", Journals],
  ["/journals/new", JournalEditor],
  ["/journals/:id", JournalEditor],
  ["/growth-plan", GrowthPlan],
  ["/growth-plan/:studentId", GrowthPlan],
  ["/forum", Forum],
  ["/forum/:id", ForumPost],
  ["/messages", Messages],
  ["/messages/:userId", Messages],
  ["/meetings", Meetings],
  ["/meetings/:id", MeetingDetail],
  ["/availability-settings", AvailabilitySettings],
  ["/book-meeting/:teacherId", BookMeeting],
  ["/teachers", Teachers],
  ["/profile", Profile],
  ["/notifications", NotificationCenter],
  ["/checkin", WeeklyCheckIn],
  ["/checkins", CheckIns],
  ["/checkin-history", CheckInHistory],
  ["/checkin-editor", CheckInFieldEditor],
  ["/versions", Versions],
  // Home portal
  ["/home", Home],
  // Community
  ["/community", Community],
  ["/community/notifications", CommunityNotifications],
  ["/community/messages", CommunityMessages],
  ["/community/profile/:userId?", CommunityProfile],
  // Admin / settings
  ["/admin", Admin],
  ["/app-settings", AppSettings],
  ["/planning-center", PlanningCenter],
  ["/sms-admin", SmsAdmin],
  // Events
  ["/events", Events],
  // Weekly Guide
  ["/guide", WeeklyGuide],
  ["/guide/archive", GuideArchive],
  ["/guide/admin", WeeklyGuideAdmin],
  // Wednesday Nights
  ["/wednesday", WednesdayNights],
  ["/wednesday/archive", WednesdayArchive],
  ["/wednesday/progress", WednesdayProgress],
  ["/wednesday/admin", WednesdayAdmin],
];

// App Router - with Emergent auth callback support for preview
function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const hasSessionId = location.hash?.includes('session_id=');
  const [maintenanceMode, setMaintenanceMode] = useState(null);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [isTeacher, setIsTeacher] = useState(false);

  // Check maintenance status on mount
  useEffect(() => {
    // CRITICAL: skip this check entirely while returning from the OAuth callback.
    // Otherwise this network round-trip delays AuthCallback's mount long enough
    // for the one-time session_id to expire, bouncing the user back to the login page.
    if (hasSessionId) return;
    const checkMaintenance = async () => {
      try {
        // Fetch maintenance status and user role in parallel
        const [maintenanceRes, userRes] = await Promise.allSettled([
          axios.get(`${API}/maintenance-status`),
          axios.get(`${API}/auth/me`),
        ]);

        const enabled = maintenanceRes.status === 'fulfilled' ? maintenanceRes.value.data.enabled : false;
        const message = maintenanceRes.status === 'fulfilled' ? maintenanceRes.value.data.message : '';
        const role = userRes.status === 'fulfilled' ? userRes.value.data.role : null;

        // Set all state at once to avoid race condition
        setMaintenanceMessage(message);
        setIsTeacher(role === 'teacher');
        setMaintenanceMode(enabled);
      } catch (error) {
        console.error('Failed to check maintenance status:', error);
        setMaintenanceMode(false);
      }
    };
    
    checkMaintenance();
  }, [hasSessionId]);

  const handleRetryMaintenance = async () => {
    try {
      const [maintenanceRes, userRes] = await Promise.allSettled([
        axios.get(`${API}/maintenance-status`),
        axios.get(`${API}/auth/me`),
      ]);

      const enabled = maintenanceRes.status === 'fulfilled' ? maintenanceRes.value.data.enabled : false;
      const message = maintenanceRes.status === 'fulfilled' ? maintenanceRes.value.data.message : '';
      const role = userRes.status === 'fulfilled' ? userRes.value.data.role : null;

      setMaintenanceMessage(message);
      setIsTeacher(role === 'teacher');
      setMaintenanceMode(enabled);
    } catch (error) {
      console.error('Failed to check maintenance status:', error);
    }
  };

  // Check URL fragment for session_id (Emergent auth callback) FIRST — before any
  // maintenance/loading gate, so the one-time session_id is exchanged immediately
  // with zero network delay. This handles the OAuth callback on ANY route for preview.
  if (hasSessionId) {
    return <AuthCallback />;
  }

  // Show loading while checking maintenance
  if (maintenanceMode === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amber-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  // Show maintenance page if enabled (teachers can bypass)
  if (maintenanceMode && !isTeacher) {
    return <MaintenancePage message={maintenanceMessage} onRetry={handleRetryMaintenance} />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/role-select"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <RoleSelect user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      {PROTECTED_PAGES.map(([path, Page]) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute>
              {({ user }) => <Page user={user} />}
            </ProtectedRoute>
          }
        />
      ))}
    </Routes>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
          <SmsQuietHoursDialog />
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                zIndex: 9999,
                background: '#fff',
                color: '#333',
              },
            }}
          />
        </BrowserRouter>
      </div>
    </GoogleOAuthProvider>
  );
}

export default App;
